import SelectionDropDown from 'components/molecules/SelectionDropDown';
import { promoRecommendation } from 'contexts/PromoRecommendationContext';
import _ from 'lodash';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import {
    calculateContainerHeight,
    calculateFontSize,
    formatNumWithKMBAlt,
    getConfig,
    parseAttributeType,
    scaleMaxValue,
    scaleMinValue,
} from 'utils/helperMethods';
import { getSummaryColumns } from 'utils/getRecommendationTableColumns';
import styled from '@emotion/styled';
import { Table } from 'antd';
import { colorConstants } from 'config/colorConstants';
import tw from 'twin.macro';
import { useFetchSummaryData } from 'hooks/promoRecommendation/useFetchSummaryData';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PROMO_RECOMMENDATION_SUMMARY_DROPDOWN_CHANGED } from 'utils/mixpanelEventNames';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { groupBarChartWidth } from 'components/organism/GrowthAnalysis/BrandValueChartContainer/BrandValueChartContainer';
import { CustomSelect } from '../BumpReport/MainSection';
import DownSvg from 'assets/svg/Down';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import Legend from 'components/molecules/Legend';
import Barchart from 'components/molecules/Barchart';
import { graphXAxisLabelFontSize } from 'utils/getSalesAttributionColumns';
import { height, subHeaderClass, yAxisLabelClass } from '../GrowthAnalysis/ExpandedView';
import { FilterContextData } from 'contexts/FilterContext';
import { UserContext } from 'contexts/ConfigContext';
import { ConfigKeys } from 'utils/Enums';
import { useFetchSummaryDrilldownData } from 'hooks/promoRecommendation/useFetchSummaryDrilldownData';

interface Props {
    planData: any;
}

export const tableHeight = calculateContainerHeight(1, 64 + 16 * 3);

export const CustomTable = styled(Table)`
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 0px;
        height: 44px;
    }
    .ant-table-body {
        overflow-y: auto !important;
    }
    // .ant-table-cell-scrollbar {
    //     display: none !important;
    // }
    .ant-table-sticky-scroll {
        position: static;
    }
    .bold {
        font-weight: bold;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        min-width: 50px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    ${tw`border-t`}
    border-bottom: unset;
    ${tw`border border-borderLinePurpuleAlt`}
`;
const yAxisGraphWidth = calculateFontSize(window.innerWidth, 60);
const graphId = `summary-grouped-chart`;
const groupedColor: [string, string] = [colorConstants.cyan, colorConstants.blue];

const SummarySection: FC<Props> = ({ planData }) => {
    const [selectedPlans, setSelectedPlans] = useState<any[]>([]);
    const [selectedKpi, setSelectedKpi] = useState<any>();
    const [selectedAttributeType, setSelectedAttributeType] = useState<any>();
    const { appConfig } = useContext(UserContext);
    const { selectedPlan } = useContext(promoRecommendation);

    const { allDimAndAttributeTypeList } = useContext(FilterContextData);
    const commonConfig = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);

    const { decimalForNonPercentage, decimalForPercentage } = commonConfig;

    const {
        data: summaryDrilldownData,
        loading: summaryDrilldownLoading,
        error: summaryDrilldownError,
        fetchDrilldownData,
    } = useFetchSummaryDrilldownData();
    const { data: summaryData, loading: summaryLoading, error: summaryError, fetchSummaryData } = useFetchSummaryData();
    const containerWidth = useContainerWidth({
        containerId: graphId,
        buffer: yAxisGraphWidth * 1 + 34 * 2,
    }); //subtracting graph width and padding on 2 sides
    const tableHeightAlt = selectedKpi ? 270 : tableHeight;

    const graphViewContainerWidth = Math.max(
        summaryDrilldownData?.chartData ? (summaryDrilldownData?.chartData.length / 2) * groupBarChartWidth : 0,
        containerWidth,
    );
    const perGraphWidth = graphViewContainerWidth / (summaryDrilldownData?.chartData?.length / 2);

    //dropdown list
    const dropdownList = useMemo(() => {
        if (planData) {
            //ignoring new plan - 1st item
            const tempPlanData = planData?.slice(1).map((item: any) => {
                return {
                    label: item?.label,
                    value: item?.planId,
                };
            });

            setSelectedPlans(
                tempPlanData?.filter((item: any) => item.label === selectedPlan.label)?.map((item: any) => item.value),
            );
            return tempPlanData;
        }
        return [];
    }, [planData, selectedPlan]);

    //dropdown checkbox handler
    function onClickCheckBoxOfPromoType(
        setSelectedPlans: React.Dispatch<React.SetStateAction<any[]>>,
        e: any,
        selectedData: any,
        selectedItems: any[],
    ) {
        if (e.target.checked && selectedItems?.length === 2) return; //max 2 selections

        setSelectedPlans((oldData: any) => {
            let data = _.cloneDeep(oldData);
            if (e.target.checked) {
                data.push(selectedData.value);
                return data;
            } else {
                let filteredList = data.filter((data: any) => data !== selectedData.value);
                return filteredList;
            }
        });
    }

    useEffect(() => {
        if (!selectedKpi && summaryData) {
            setSelectedKpi(summaryData?.[0]);
        }
    }, [summaryData]);

    useEffect(() => {
        if (selectedPlans && Array.isArray(selectedPlans)) {
            fetchSummaryData({
                variables: {
                    planIds: selectedPlans,
                },
            });
        }
        if (selectedPlans?.length === 0) setSelectedKpi(undefined);
    }, [selectedPlans]);

    //summary table columns
    const summaryTableColumns = useMemo(() => {
        return getSummaryColumns(summaryData, setSelectedKpi, selectedKpi);
    }, [summaryData, selectedKpi]);

    //fetching 2nd dropdown values
    const attributeTypeDropdownList = useMemo(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions']
        ) {
            let data = parseAttributeType(allDimAndAttributeTypeList['dimentions']);
            if (data.length) {
                setSelectedAttributeType(data[4]); //brand
                return data;
            }
        }
        return [];
    }, [allDimAndAttributeTypeList?.loading]);

    useEffect(() => {
        if (selectedKpi) {
            fetchDrilldownData({
                variables: {
                    planIds: selectedPlans,
                    attributeType: _.snakeCase(selectedAttributeType?.label),
                    dimension: selectedAttributeType?.dimension,
                    kpiName: _.snakeCase(selectedKpi?.dataKey),
                },
            });
        }
    }, [selectedKpi, selectedPlans, selectedAttributeType]);

    const isChartGrouped = selectedPlans?.length === 2;

    return (
        <section>
            <div>
                <div className="mb-4">
                    <SelectionDropDown
                        className="dropdown-width-alt"
                        selectAll={false}
                        list={dropdownList}
                        checkedListObject={selectedPlans}
                        eventMixPanelString={PROMO_RECOMMENDATION_SUMMARY_DROPDOWN_CHANGED}
                        disabled={summaryLoading}
                        setSelectedList={setSelectedPlans}
                        customOnChange={onClickCheckBoxOfPromoType}
                        selectedList={selectedPlans}
                    />
                </div>
                <CustomTable
                    loading={summaryLoading}
                    //bordered
                    pagination={false}
                    scroll={{ x: '100%', y: tableHeightAlt }}
                    sticky
                    columns={summaryTableColumns}
                    dataSource={summaryData}
                    // loading={tableLoading}
                />
            </div>
            {selectedKpi && (
                <div className={`mt-4`}>
                    <p className={subHeaderClass}>{selectedKpi?.KPI}</p>
                    <div className="flex items-center gap-2 mt-2">
                        <p>Dimension</p>
                        <CustomSelect
                            className="dropdown-height dropdown-width-multi-select uniqSelPTRows"
                            suffixIcon={<DownSvg />}
                            value={selectedAttributeType?.value}
                            disabled={summaryLoading}
                            options={attributeTypeDropdownList}
                            onChange={(val: any, item: any) => {
                                setSelectedAttributeType(item);
                            }}
                        />
                    </div>
                    {summaryDrilldownError && !summaryDrilldownLoading && !summaryDrilldownData ? (
                        <div
                            style={{
                                height: height + 150,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <ErrorBox msg={String(summaryDrilldownError)} />
                        </div>
                    ) : // IF LOADING RETURN LOADER
                    summaryDrilldownLoading ? (
                        <div
                            style={{
                                height: height + 150,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Loader />
                        </div>
                    ) : (
                        <>
                            <div className="flex mt-1 justify-between">
                                <p className={`w-[160px] ${yAxisLabelClass}`}>
                                    {selectedKpi?.KPI}
                                    {`${selectedKpi?.formatting?.pre ? ` ${selectedKpi?.formatting?.pre}` : ''}`}
                                </p>
                                <Legend data={summaryDrilldownData?.legend || []} />
                                <p className={`w-[160px] ${yAxisLabelClass}`}></p>
                            </div>
                            <div className="flex ml-1 mt-2">
                                {/* Y AXIS */}
                                <Barchart
                                    barWidth={31}
                                    xAxisConfig={false}
                                    dataset={[]}
                                    height={height}
                                    loading={false}
                                    chartContainer={`promo-landscape-y1-${1}`}
                                    fields={['category', 'sales']}
                                    minBound={scaleMinValue(30, summaryDrilldownData?.minVal)}
                                    maxBound={scaleMaxValue(30, summaryDrilldownData?.maxVal)}
                                    width={yAxisGraphWidth}
                                    colorKey={'type'}
                                    yAxisLabelFormatter={(val: any) => {
                                        return formatNumWithKMBAlt(
                                            val,
                                            decimalForNonPercentage,
                                            decimalForNonPercentage,
                                        );
                                    }}
                                    YGrid={false}
                                />
                                {/* MAIN CONTAINER */}
                                <div
                                    id={graphId}
                                    style={{
                                        overflowX: 'auto',
                                        maxWidth: '100%',
                                        paddingBottom: 5,
                                    }}
                                >
                                    <Barchart
                                        barWidth={isChartGrouped ? 15 : 30}
                                        dataset={
                                            summaryDrilldownData?.chartData?.length > 0
                                                ? summaryDrilldownData?.chartData
                                                : []
                                        }
                                        height={height}
                                        loading={false}
                                        chartContainer={`promo-landscape-x-${1}`}
                                        fields={['category', 'sales', 'type', 'roi']}
                                        minBound={scaleMinValue(30, summaryDrilldownData?.minVal)}
                                        maxBound={scaleMaxValue(30, summaryDrilldownData?.maxVal)}
                                        width={
                                            perGraphWidth * (summaryDrilldownData?.chartData?.length / 2) || //removing total data
                                            window.innerWidth
                                        }
                                        tooltipFormatter={(xAxis, val) => {
                                            return {
                                                name: `${xAxis}`,
                                                value: formatNumWithKMBAlt(
                                                    val,
                                                    decimalForNonPercentage,
                                                    decimalForNonPercentage,
                                                ),
                                            };
                                        }}
                                        Xline={false}
                                        colorKey={isChartGrouped ? 'type' : 'color'}
                                        disableIntervalLabel
                                        enableGroupedChart={isChartGrouped}
                                        groupedChartColor={groupedColor}
                                    />
                                    <div className="flex">
                                        {summaryDrilldownData?.chartData?.map((item: any, index: number) => {
                                            if (index % 2 === 0)
                                                return (
                                                    <div
                                                        style={{
                                                            width: perGraphWidth,
                                                            minWidth: perGraphWidth,
                                                            overflowWrap: 'anywhere',
                                                            fontSize: graphXAxisLabelFontSize,
                                                        }}
                                                        className="text-center p-[2px]"
                                                        key={item?.category}
                                                    >
                                                        {item?.category}
                                                    </div>
                                                );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </section>
    );
};

export default SummarySection;
